import { useState, useEffect, useRef } from "react";
import { X, Sparkles, Send, Loader2, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AIMarkdown } from "@/components/revenueos/AIMarkdown";

/**
 * Ask Saarv — a modal-drawer conversational AI trigger.
 * Replaces the old "Copilot" side panel. Opens over content with focus on input.
 */
export function AskSaarv({ open, onOpenChange, seedMessage }) {
  const [sessionId] = useState(() => `ask-${Date.now()}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      if (seedMessage) setInput(seedMessage);
    }
  }, [open, seedMessage]);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  async function send(text) {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/copilot/message", {
        session_id: sessionId,
        message: msg,
        context_type: "dashboard",
      });
      setMessages(m => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error("Ask Saarv failed — try again");
    } finally { setLoading(false); }
  }

  const SUGGESTIONS = [
    "Which deals are at risk?",
    "What should I do today?",
    "Summarize my top opportunities",
    "How is partner performance?",
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-16 md:pt-24 px-4" onClick={() => onOpenChange(false)} data-testid="ask-saarv-modal">
      <div
        className="w-full max-w-2xl bg-white border border-black/10 rounded-md shadow-2xl flex flex-col max-h-[85vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-black/10">
          <Sparkles size={14} className="text-[var(--copper)]" />
          <div className="font-display text-[14px] flex-1 text-[var(--saarv-blue)]">Ask Saarv</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-dim)] font-mono hidden sm:block">Claude Sonnet 4.5</div>
          <button onClick={() => onOpenChange(false)} data-testid="ask-close" className="text-[var(--text-dim)] hover:text-[var(--saarv-blue)]"><X size={16} /></button>
        </div>

        {/* Input at top */}
        <div className="px-4 pt-4">
          <div className="flex items-end gap-2 bg-[var(--saarv-blue-soft)] border border-[var(--saarv-blue)]/15 focus-within:border-[var(--saarv-blue)] rounded-md px-3 py-2.5 transition-colors">
            <MessageCircle size={14} className="text-[var(--copper)] mt-1 shrink-0" />
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about your pipeline, deals, partners, tasks…"
              rows={1}
              data-testid="ask-input"
              className="flex-1 bg-transparent outline-none resize-none text-[14px] placeholder:text-[var(--text-dim)] text-[var(--text)] max-h-32"
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              data-testid="ask-send"
              className="text-[var(--copper)] disabled:opacity-40"
              aria-label="Send"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>

        {/* Suggestions */}
        {messages.length === 0 && !loading && (
          <div className="px-4 py-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map(s => (
              <button
                key={s}
                onClick={() => send(s)}
                data-testid={`ask-suggest-${s.slice(0, 12).replace(/\s/g, "-")}`}
                className="text-[11px] px-2.5 py-1 rounded border border-black/10 bg-white hover:border-[var(--saarv-blue)]/40 hover:bg-[var(--saarv-blue-soft)] transition-colors text-[var(--text-muted)]"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 pb-4 space-y-3" data-testid="ask-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-md px-3 py-2 ${
                m.role === "user"
                  ? "bg-[var(--saarv-blue)] text-white text-[13px] leading-relaxed whitespace-pre-wrap"
                  : "bg-[var(--saarv-blue-soft)] border border-[var(--saarv-blue)]/15 text-[var(--text)]"
              }`}>
                {m.role === "user" ? m.content : <AIMarkdown>{m.content}</AIMarkdown>}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-[12px] text-[var(--text-muted)]">
              <Loader2 size={12} className="animate-spin text-[var(--copper)]" /> Thinking…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
